import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import type { AgentV2UIState, AgentV2SearchDefaults, MatterArtifactVersionSelection } from '@/agent-v2/store/schemas'

export enum AgentV2TransportPhase {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  OPEN = 'open',
  DETACHED = 'detached',
}

export type AgentV2TransportEntry = {
  clientRequestId: string | null
  phase: AgentV2TransportPhase
  stopSent: boolean
}

export type AgentV2TransportState = {
  byConversationId: Record<string, AgentV2TransportEntry | undefined>
}

export type AgentV2StartTurnIntent = {
  assistantMessageId: string
  clientRequestId: string
  conversationId: string
  inputText: string
  matterId: string
  uiState?: AgentV2UIState
  matterArtifactSelection?: MatterArtifactVersionSelection
  selectedSkillIds?: string[]
  selectedPaxtonSkillNames?: string[]
  searchDefaults: AgentV2SearchDefaults
  /* Resends an earlier turn as it was, so it doesn't change the chat's files or the composer */
  replaysEarlierTurn?: boolean
  userMessageId: string
}

const initialState: AgentV2TransportState = {
  byConversationId: {},
}

const agentV2TransportSlice = createSlice({
  name: 'agentV2TransportState',
  initialState,
  reducers: {
    startTurnRequested: (state, action: PayloadAction<AgentV2StartTurnIntent>) => {
      const current = state.byConversationId[action.payload.conversationId]
      if (current && current.phase !== AgentV2TransportPhase.IDLE) return

      state.byConversationId[action.payload.conversationId] = {
        clientRequestId: action.payload.clientRequestId,
        phase: AgentV2TransportPhase.CONNECTING,
        stopSent: false,
      }
    },
    stopTurnRequested: (_state, _action: PayloadAction<{ conversationId: string }>) => {},
    closeConversationTransport: (state, action: PayloadAction<{ conversationId: string }>) => {
      delete state.byConversationId[action.payload.conversationId]
    },
    socketOpened: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string }>) => {
      const current = state.byConversationId[action.payload.conversationId]
      if (current?.clientRequestId !== action.payload.clientRequestId) return
      current.phase = AgentV2TransportPhase.OPEN
    },
    stopSent: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string }>) => {
      const current = state.byConversationId[action.payload.conversationId]
      if (current?.clientRequestId !== action.payload.clientRequestId || current.phase !== AgentV2TransportPhase.OPEN) return
      current.stopSent = true
    },
    socketDetached: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string }>) => {
      const current = state.byConversationId[action.payload.conversationId]
      if (current?.clientRequestId !== action.payload.clientRequestId) return
      current.phase = AgentV2TransportPhase.DETACHED
    },
    transportSettled: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string }>) => {
      const current = state.byConversationId[action.payload.conversationId]
      if (current?.clientRequestId !== action.payload.clientRequestId) return
      state.byConversationId[action.payload.conversationId] = {
        clientRequestId: null,
        phase: AgentV2TransportPhase.IDLE,
        stopSent: false,
      }
    },
    closeAllTransports: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(AgentV2ConversationsActions.conversationDeletionStarted, (state, action) => {
        delete state.byConversationId[action.payload.conversationId]
      })
      .addCase(AgentV2ConversationsActions.deleteConversation, (state, action) => {
        delete state.byConversationId[action.payload.conversationId]
      })
      .addCase(AgentV2ConversationsActions.nullifyData, () => initialState)
  },
})

export const AgentV2TransportActions = agentV2TransportSlice.actions
export default agentV2TransportSlice.reducer
